/**
 * Draft session state machine -- advanceDraftSessionWithPoint and
 * advanceDraftSessionWithText handle the multi-step interactive
 * drafting workflow (LINE, CIRCLE, DIMENSION, etc.)
 *
 * Extracted from draftingHelpers.ts -- pure structural refactor.
 *
 * ## Function dependency map
 *
 * advanceDraftSessionWithPoint(session, point)
 *   -> createPreviewEntity({ ... })
 *   -> resolveArcGeometry(points) -> toStageAngle -> normalizeAngle
 *                                 -> isAngleOnSweep
 *   -> rectangleCorners(p1, p2) -> corner array
 *   -> nextDraftEntityId(session)
 *   -> formatPoint / formatPointList
 *   -> distanceBetween (from draftingHelpers.ts)
 *
 * advanceDraftSessionWithText(session, text)
 *   -> parseDraftPointInput(session, text)
 *       -> parsePointToken (from coordinate-input)
 *       -> referencePointForDraftTextInput (from draftingHelpers.ts)
 *   -> advanceDraftSessionWithPoint (recursive for parsed coords)
 *   -> createPreviewEntity / nextDraftEntityId / formatPoint
 *   -> isPositiveFiniteNumber (validation)
 *   -> defaultDraftProperties / draftStepAcceptsPointInput (from draftingHelpers.ts)
 *
 * Private helpers:
 *   createPreviewEntity(input) -> EntityPreview
 *   resolveArcGeometry(points) -> { center, radius, startAngle, endAngle, ccw }
 *   rectangleCorners(p1, p2) -> DraftPoint[4]
 *   withZ(point) -> point with z:0
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { parsePointToken } from "@dxf-ai-editor/coordinate-input";
import { computeModelExtent, type ModelExtent } from "@dxf-ai-editor/command-engine";
import {
  findBoundaryFromSeed,
  isAnonymousBlockName,
  type BoundaryLoop,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";
import { t } from "../i18n";
import type {
  DraftPoint,
  DraftSession,
  DraftStep,
  DraftCommitResult,
} from "./draftTypes";
import { distanceBetween } from "./draftTypes";
import {
  arcSceEndPoint,
  circumscribingCircleFromThree,
  defaultDraftProperties,
  draftStepAcceptsPointInput,
  generatePolygonVertices,
  referencePointForDraftTextInput,
} from "./draftSharedHelpers";
// Phase 5.3.f — modify steps now dispatch via the unified
// `ADVANCE_WITH_POINT` / `ADVANCE_WITH_TEXT` registries; the
// `isModifyStep` predicate + delegation blocks are gone.
// `advance*` functions stay imported by the wrapper module
// `modifyRegistry.ts`.
// Phase 5.3.d — dimension steps now dispatch via the unified
// `ADVANCE_WITH_POINT` registry; the `isDimensionStep` predicate +
// delegation block in `advanceDraftSessionWithPoint` is gone. The
// `advanceDimensionWithPoint` function itself is still imported by
// the registry wrapper module (`dimensionRegistry.ts`).
// Phase 5.3.e — block-authoring steps now dispatch via the unified
// `ADVANCE_WITH_POINT` / `ADVANCE_WITH_TEXT` registries; the
// `isBlockAuthoringStep` predicate + delegation blocks are gone.
// `advance*` functions stay imported by the wrapper module
// `blockAuthoringRegistry.ts`.
import {
  advanceWithPointFromRegistry,
  advanceWithTextFromRegistry,
} from "./drafting";
import {
  interceptPointFilterPoint,
  interceptPointFilterText,
} from "./pointFilterSession";
import {
  interceptTemporaryTrackPointPoint,
  interceptTemporaryTrackPointText,
} from "./otrackTrackPointSession";

// Conventional model-space id used throughout the shell state. MVIEW
// commits read the extent of all entities in this space so the new
// viewport's view centre / view height auto-fit model contents — see
// AutoCAD MVIEW "FIT" default behaviour.
const MODEL_SPACE_ID = "model";

/**
 * Compute the model-space extent consumed by `geometry/create-viewport`.
 * Returns `undefined` (not `null`) so callers can spread this value
 * directly into the command payload with `...(ext ? { modelExtent: ext } : {})`
 * under `exactOptionalPropertyTypes`.
 */
function modelExtentFromEntities(
  entities: EntityPreview[],
): ModelExtent | undefined {
  const modelEntities = entities.filter(
    (entity) => entity.spaceId === MODEL_SPACE_ID,
  );
  return computeModelExtent(modelEntities) ?? undefined;
}

export function advanceDraftSessionWithPoint(
  session: DraftSession,
  point: DraftPoint,
  existingEntities: EntityPreview[],
  activeSpaceId: string,
  _defaultObjectProperties: Record<string, string>,
  /**
   * Phase E (HATCH internal-pick): full DrawingEntity[] for the active
   * space. The flat EntityPreview shape used by other steps cannot be
   * fed to `findBoundaryFromSeed` (it lacks geometric primitives like
   * line endpoints / arc center / radius). When omitted, internal-pick
   * boundary detection is silently skipped — preserves backwards
   * compatibility for unit tests.
   */
  geometryEntities: ReadonlyArray<DrawingEntity> = [],
  /**
   * VPCLIP-INTERACTIVE — see {@link AdvanceWithPointContext.paperOrigin}.
   * Optional / defaults to `null` so every pre-existing caller (unit
   * tests included) that doesn't pass it observes byte-identical
   * behaviour.
   */
  paperOrigin: { x: number; y: number } | null = null,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  // Phase 5.3.d/e/f — dimension + block-authoring + modify
  // delegations all deleted; every step dispatches via the unified
  // `ADVANCE_WITH_POINT` registry below.

  // OTRACK-SUBSYSTEM — `TT` (Temporary Track Point, ТЗ §10.4) interception.
  // Runs BEFORE the point-filter layer: while a TT sub-prompt is active it
  // must exclusively own the pick (the captured point is pushed to the
  // acquired-point store and never composed with FROM/M2P); when TT is NOT
  // active this always returns null and the point-filter layer below runs
  // byte-identically to before.
  const ttPoint = interceptTemporaryTrackPointPoint(session, point);
  if (ttPoint) return ttPoint;

  // Point-filter / temporary-tracking interception (FROM / M2P / .X.Y.Z —
  // ТЗ §8.6/§10.4). A pick captured mid-flow feeds the tracking layer; once
  // the composed point resolves it re-enters this dispatcher with the
  // cleared session (no filter → falls straight through to the registry).
  const filteredPoint = interceptPointFilterPoint(session, point, (s, p) =>
    advanceDraftSessionWithPoint(
      s,
      p,
      existingEntities,
      activeSpaceId,
      _defaultObjectProperties,
      geometryEntities,
      paperOrigin,
    ),
  );
  if (filteredPoint) return filteredPoint;

  // Track 2 Phase 5: 59-case switch -> registry-driven dispatch via
  // ADVANCE_WITH_POINT lookup table. Steps not in the registry fall
  // through to the legacy default ({ nextSession: session }).
  const registryResult = advanceWithPointFromRegistry(session, point, {
    activeSpaceId,
    existingEntities,
    geometryEntities,
    paperOrigin,
  });
  if (registryResult !== null) return registryResult;
  return { nextSession: session };
}

export function advanceDraftSessionWithText(
  session: DraftSession,
  rawText: string,
  existingEntities: EntityPreview[],
  activeSpaceId: string,
  defaultObjectProperties: Record<string, string>,
  /**
   * Same role as in `advanceDraftSessionWithPoint` — only consumed by
   * the HATCH `hatch-pick-internal-point` recursive path through
   * `advanceDraftSessionWithPoint`.
   */
  geometryEntities: ReadonlyArray<DrawingEntity> = [],
  /** VPCLIP-INTERACTIVE — see `advanceDraftSessionWithPoint`'s param. */
  paperOrigin: { x: number; y: number } | null = null,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  // Phase 5.3.e/f — block-authoring + modify delegations deleted;
  // every step dispatches via the unified `ADVANCE_WITH_TEXT`
  // registry through `advanceWithTextFromRegistry` below.

  const text = rawText.trim();
  const normalizedText = text.toUpperCase();
  const defaults = defaultDraftProperties(defaultObjectProperties);

  // OTRACK-SUBSYSTEM — `TT` interception, ahead of the point-filter layer
  // for the same reason as the point dispatcher above.
  const ttText = interceptTemporaryTrackPointText(session, text);
  if (ttText) return ttText;

  // Point-filter / temporary-tracking interception (FROM / M2P / .X.Y.Z —
  // ТЗ §8.6/§10.4). Runs first so a filter starter / continuation / cancel
  // is handled before the LINE-close / PLINE-close / keyword branches. When
  // the composed point resolves it re-enters the point dispatcher with the
  // cleared session.
  const filteredText = interceptPointFilterText(session, text, (s, p) =>
    advanceDraftSessionWithPoint(
      s,
      p,
      existingEntities,
      activeSpaceId,
      defaultObjectProperties,
      geometryEntities,
      paperOrigin,
    ),
  );
  if (filteredText) return filteredText;

  if (session.command === "LINE") {
    if (text === "") {
      return {
        nextSession: null,
      };
    }

    if (
      (normalizedText === "C" || normalizedText === "CLOSE") &&
      session.points.length >= 2
    ) {
      const closingStart = session.points.at(-1) ?? session.points[0];
      const closingEnd = session.points[0];

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-line",
            source: "scene",
            spaceId: activeSpaceId,
            start: withZ(closingStart),
            end: withZ(closingEnd),
          },
          summary: t("summary.line_closed"),
          commandText: "LINE",
        },
      };
    }
  }

  if (session.command === "PLINE") {
    if (text === "" && session.points.length >= 2) {
      // Phase C.1 — read per-vertex widths from session.options (filled
      // in by the W sub-flow + polyline-first-point / polyline-next-point
      // handlers). Falls back to 0/0 if the array is shorter than the
      // points list (defensive — shouldn't happen with the new handlers).
      let widthArrayCommit: { startWidth: number; endWidth: number }[] = [];
      if (session.options.polylineWidths) {
        try {
          const parsed = JSON.parse(session.options.polylineWidths);
          if (Array.isArray(parsed)) {
            widthArrayCommit = parsed.filter(
              (w): w is { startWidth: number; endWidth: number } =>
                typeof w === "object" &&
                w !== null &&
                typeof (w as { startWidth: unknown }).startWidth === "number" &&
                typeof (w as { endWidth: unknown }).endWidth === "number",
            );
          }
        } catch {
          widthArrayCommit = [];
        }
      }
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/create-lwpolyline",
            source: "scene",
            spaceId: activeSpaceId,
            vertices: session.points.map((p, idx) => ({
              x: p.x,
              y: p.y,
              bulge: 0,
              startWidth: widthArrayCommit[idx]?.startWidth ?? 0,
              endWidth: widthArrayCommit[idx]?.endWidth ?? 0,
            })),
            closed: session.closed,
          },
          summary: session.closed
            ? t("summary.pline_closed")
            : t("summary.pline_finished"),
          commandText: "PLINE",
        },
      };
    }

    if (
      (normalizedText === "C" || normalizedText === "CLOSE") &&
      session.points.length >= 2
    ) {
      return {
        nextSession: {
          ...session,
          closed: true,
          hoverPoint: null,
        },
      };
    }

    if (
      (normalizedText === "U" || normalizedText === "UNDO") &&
      session.points.length >= 2
    ) {
      return {
        nextSession: {
          ...session,
          points: session.points.slice(0, -1),
          hoverPoint: null,
        },
      };
    }

    // ── PLINE Width sub-flow (Phase C.1) ─────────────────────────
    // Typing `W` / `Width` / `Ш` / `Ширина` at the first or next
    // point step enters the start-width prompt.
    if (
      (session.step === "polyline-first-point" ||
        session.step === "polyline-next-point") &&
      (normalizedText === "W" ||
        normalizedText === "WIDTH" ||
        normalizedText === "Ш" ||
        normalizedText === "ШИРИНА")
    ) {
      return {
        nextSession: {
          ...session,
          step: "polyline-width-start",
          hoverPoint: null,
          // Remember which step to return to after both widths are
          // collected (first-point if no points yet, next-point
          // otherwise — matches the AutoCAD prompt sequence).
          options: {
            ...session.options,
            polylineWidthReturnStep:
              session.points.length === 0
                ? "polyline-first-point"
                : "polyline-next-point",
          },
        },
      };
    }

    // PLINE width prompts — both fields are typed scalars.
    if (session.step === "polyline-width-start") {
      const startWidthValue = extractScalarFromDraftText(text);
      if (
        startWidthValue === null ||
        !Number.isFinite(startWidthValue) ||
        startWidthValue < 0
      ) {
        return { nextSession: session };
      }
      return {
        nextSession: {
          ...session,
          step: "polyline-width-end",
          hoverPoint: null,
          options: {
            ...session.options,
            currentStartWidth: String(startWidthValue),
          },
        },
      };
    }
    if (session.step === "polyline-width-end") {
      const endWidthValue = extractScalarFromDraftText(text);
      if (
        endWidthValue === null ||
        !Number.isFinite(endWidthValue) ||
        endWidthValue < 0
      ) {
        return { nextSession: session };
      }
      // Resume the polyline drawing flow at the prior step. Both
      // widths now live in session.options and will be applied when
      // the next vertex is created.
      const returnStepRaw = session.options.polylineWidthReturnStep ?? "";
      const returnStep: DraftStep =
        returnStepRaw === "polyline-first-point" ||
        returnStepRaw === "polyline-next-point"
          ? returnStepRaw
          : session.points.length === 0
            ? "polyline-first-point"
            : "polyline-next-point";
      return {
        nextSession: {
          ...session,
          step: returnStep,
          hoverPoint: null,
          options: {
            ...session.options,
            currentEndWidth: String(endWidthValue),
          },
        },
      };
    }
  }

  if (
    session.step === "circle-radius" &&
    (normalizedText === "D" || normalizedText === "DIAMETER")
  ) {
    return {
      nextSession: {
        ...session,
        step: "circle-diameter",
        hoverPoint: null,
      },
    };
  }

  // CIRCLE keyword routing on the initial center-prompt: typing "2P" /
  // "2POINT" / "2Т" (RU) at `circle-center` switches into the 2-point
  // diameter sub-flow. AutoCAD parity — the prompt offers [3P / 2P / Ttr]
  // alongside the default "specify center". The keyword must be matched
  // before the generic point-parser falls through, otherwise "2P" parses
  // as no point and the session stays put.
  if (
    session.step === "circle-center" &&
    (normalizedText === "2P" ||
      normalizedText === "2POINT" ||
      normalizedText === "2Т" ||
      normalizedText === "2T")
  ) {
    return {
      nextSession: {
        ...session,
        step: "circle-2p-first-point",
        hoverPoint: null,
      },
    };
  }

  // CIRCLE 3-Point sub-flow keyword (Phase C.2). The first picked
  // point reuses the existing `circle-center` slot to keep all three
  // clicks consistent (session.points becomes [p1, p2, p3]). After
  // typing `3P`, the very next click goes to `circle-3p-second-point`
  // — but since `circle-center` is the source step and clicks on
  // `circle-center` advance to `circle-radius`, we instead switch to
  // a dedicated entry where the FIRST click becomes p1.
  if (
    session.step === "circle-center" &&
    (normalizedText === "3P" ||
      normalizedText === "3POINT" ||
      normalizedText === "3Т" ||
      normalizedText === "3T")
  ) {
    return {
      nextSession: {
        ...session,
        // Use the existing circle-center step but set a flag so the
        // first click routes into the 3P flow. To avoid plumbing a
        // special flag through circle-center, we instead use a
        // separate entry step `circle-3p-first-point` (treated like
        // circle-center for point input but advancing to second).
        // The minimal viable implementation: re-route circle-center
        // first click → circle-3p-second-point via an option flag.
        step: "circle-center",
        hoverPoint: null,
        options: { ...session.options, circle3PMode: "true" },
      },
    };
  }

  // ── ARC Start-Center-End (SCE) sub-flow keyword (Phase C.3) ───────
  // Typing `C` / `Center` / `Ц` / `Центр` at the second-point step
  // switches into SCE mode — the next click is the arc's center, and
  // the user then types a subtended angle to commit the arc.
  if (
    session.step === "arc-second" &&
    (normalizedText === "C" ||
      normalizedText === "CENTER" ||
      normalizedText === "Ц" ||
      normalizedText === "ЦЕНТР")
  ) {
    return {
      nextSession: {
        ...session,
        step: "arc-sce-center",
        hoverPoint: null,
      },
    };
  }

  // Track 2 Phase 5b: registry-driven step-only dispatch via
  // ADVANCE_WITH_TEXT lookup table. Handles all per-step text-input
  // logic that doesn't depend on cross-cutting command/keyword
  // patterns above. Returns:
  //   - non-null AdvanceResult → handled, return immediately
  //   - null                   → fall through to point-parser /
  //                              cross-cutting tail (revcloud-ready,
  //                              wipeout-ready, final default)
  const registryTextResult = advanceWithTextFromRegistry(session, {
    text,
    normalizedText,
    activeSpaceId,
    existingEntities,
    geometryEntities,
    paperOrigin,
  });
  if (registryTextResult !== null) return registryTextResult;

  // ── RECTANGLE Dimensions sub-flow keyword (Phase C.4) ─────────────
  // Typing `D` / `Dimensions` / `Р` / `Размеры` at first-corner OR
  // second-corner switches into dimensions input mode. AutoCAD
  // accepts the keyword on both steps.
  if (
    (session.step === "rectangle-first-corner" ||
      session.step === "rectangle-second-corner") &&
    (normalizedText === "D" ||
      normalizedText === "DIMENSIONS" ||
      normalizedText === "Р" ||
      normalizedText === "РАЗМЕРЫ")
  ) {
    return {
      nextSession: {
        ...session,
        step: "rectangle-d-length",
        hoverPoint: null,
      },
    };
  }

  if (text !== "" && draftStepAcceptsPointInput(session.step)) {
    const parsedPoint = parseDraftPointInput(session, text);

    if (parsedPoint) {
      return advanceDraftSessionWithPoint(
        session,
        parsedPoint,
        existingEntities,
        activeSpaceId,
        defaultObjectProperties,
        geometryEntities,
        paperOrigin,
      );
    }
  }

  return {
    nextSession: session,
  };
}

function isPositiveFiniteNumber(text: string): boolean {
  const value = Number(text);
  return Number.isFinite(value) && value > 0;
}

/**
 * Extract a scalar numeric value from a draft-text input.
 *
 * AutoCAD-parity background: Dimension Input fields configured with
 * `kind: "length"` (e.g. `text-height`, `block-scale`, `insert-specify-scale`,
 * `scale-factor`) build their commit token via `buildDimCommitToken` as a
 * polar coordinate `@<value><angle>` — even when the underlying step expects
 * a plain number. State-machine handlers that read scalar values via
 * `parseFloat(text)` then receive `NaN` and refuse to advance, freezing the
 * session.
 *
 * This helper recognises both forms:
 *   - raw number  → `Number(text)`
 *   - polar token → `@<value><angle>` → returns `value`
 *   - cartesian   → `@<dx>,<dy>` → returns `Math.hypot(dx, dy)` (length of vector)
 *   - empty       → `null`
 *
 * Returns `null` when the input is neither a finite number nor a recognised
 * coord token. Callers decide whether the value is allowed to be zero/negative.
 */
function extractScalarFromDraftText(rawText: string): number | null {
  const text = rawText.trim();
  if (text === "") return null;

  // Polar form `@d<a` or bare `d<a`: use the magnitude (distance).
  const ltIdx = text.indexOf("<");
  if (ltIdx >= 0) {
    const head = text.slice(0, ltIdx).replace(/^[@#]/, "");
    const value = Number(head);
    if (Number.isFinite(value)) return value;
    return null;
  }

  // Cartesian form `@dx,dy` or bare `dx,dy`: use the vector length.
  const commaIdx = text.indexOf(",");
  if (commaIdx >= 0) {
    const head = text.slice(0, commaIdx).replace(/^[@#]/, "");
    const tail = text.slice(commaIdx + 1);
    const dx = Number(head);
    const dy = Number(tail);
    if (Number.isFinite(dx) && Number.isFinite(dy)) {
      return Math.hypot(dx, dy);
    }
    return null;
  }

  // Plain number — strip leading @ / # if a stray prefix slipped in.
  const stripped = text.replace(/^[@#]/, "");
  const value = Number(stripped);
  return Number.isFinite(value) ? value : null;
}

/**
 * FIX-4 Cluster B helper — assemble a `geometry/create-block-insert`
 * commit from the INSERT session's accumulated options and end the
 * draft session.
 *
 * The INSERT specify-on-screen flow (insert-specify-point →
 * insert-specify-scale → insert-specify-rotation → insert-collect-attribs)
 * gathers the parameters needed for `geometry/create-block-insert` into
 * `session.options`. Previously the terminal text-input step routed the
 * session to `insert-dialog`, expecting the dialog effect in App.tsx
 * to commit — but the dialog only commits on the user's explicit OK
 * click, so the entity was stranded uncreated. This helper short-
 * circuits that path and dispatches the commit directly with the
 * collected values.
 *
 * `overrides` lets the caller inject the just-typed value (scaleX or
 * rotation) without serialising it back into options first.
 */
function finalizeInsertSession(
  session: DraftSession,
  activeSpaceId: string,
  overrides: { scaleX?: number; rotation?: number },
): { nextSession: DraftSession | null; commit: DraftCommitResult } {
  const blockName = session.options.blockName ?? "";
  const insertionX = parseFloat(session.options.insertionX ?? "");
  const insertionY = parseFloat(session.options.insertionY ?? "");
  const optScaleX = parseFloat(session.options.scaleX ?? "");
  const optScaleY = parseFloat(session.options.scaleY ?? "");
  const optRotation = parseFloat(session.options.rotation ?? "");
  const uniformScale = session.options.uniformScale === "true";

  const scaleX =
    overrides.scaleX !== undefined && Number.isFinite(overrides.scaleX)
      ? overrides.scaleX
      : Number.isFinite(optScaleX)
        ? optScaleX
        : 1;
  const scaleY = uniformScale
    ? scaleX
    : Number.isFinite(optScaleY)
      ? optScaleY
      : scaleX;
  const rotation =
    overrides.rotation !== undefined && Number.isFinite(overrides.rotation)
      ? overrides.rotation
      : Number.isFinite(optRotation)
        ? optRotation
        : 0;

  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/create-block-insert",
        source: "scene",
        spaceId: activeSpaceId,
        blockName: blockName || "BLOCK_REF",
        insertionPoint: {
          x: Number.isFinite(insertionX) ? insertionX : 0,
          y: Number.isFinite(insertionY) ? insertionY : 0,
        },
        scaleX,
        scaleY,
        rotation,
      },
      summary: t("summary.insert_created"),
      commandText: "INSERT",
    },
  };
}

function parseDraftPointInput(
  session: DraftSession,
  rawText: string,
): DraftPoint | null {
  const referencePoint = referencePointForDraftTextInput(session);
  const directionPoint =
    session.step === "line-next-point" || session.step === "polyline-next-point"
      ? (session.hoverPoint ?? session.points.at(-2) ?? null)
      : (session.hoverPoint ?? null);
  const parsedPoint = parsePointToken(rawText, {
    referencePoint: referencePoint ? { ...referencePoint, z: 0 } : null,
    directionPoint:
      directionPoint && referencePoint ? { ...directionPoint, z: 0 } : null,
  });

  if (!parsedPoint.ok) {
    return null;
  }

  return {
    x: parsedPoint.value.x,
    y: parsedPoint.value.y,
  };
}

// Pure helpers (`createPreviewEntity`, `rectangleCorners`, `formatPoint`,
// `formatPointList`, `formatNumber`, `resolveArcGeometry`, `toStageAngle`,
// `normalizeAngle`) live in `draftSharedHelpers.ts` so
// `draftPreviewEntities.ts` can pull them in without re-importing this
// module (was a depcruise cycle source). Re-exported below for
// back-compat.
export {
  createPreviewEntity,
  rectangleCorners,
  formatPoint,
  formatPointList,
  formatNumber,
  resolveArcGeometry,
  normalizeAngle,
  toStageAngle,
} from "./draftSharedHelpers";

import {
  createPreviewEntity,
  rectangleCorners,
  formatPoint,
  formatPointList,
  formatNumber,
  resolveArcGeometry,
} from "./draftSharedHelpers";

function nextDraftEntityId(
  existingEntities: EntityPreview[],
  prefix: string,
): string {
  const existingIds = new Set(existingEntities.map((entity) => entity.id));
  let counter = 1;

  while (existingIds.has(`${prefix}-${counter}`)) {
    counter += 1;
  }

  return `${prefix}-${counter}`;
}

function withZ(point: DraftPoint) {
  return {
    x: point.x,
    y: point.y,
    z: 0,
  };
}
